import {
  FC,
  useCallback,
  useEffect,
  useState,
  useRef,
} from "react";
import {
  BooleanFilter,
  TableAbstract,
  TextFilter,
} from "shared/components/Components";

import { useMercuryContext } from "../../context";

import {
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
} from "@ant-design/icons";
import TimezoneConverter from "components/timezone-converter";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ROUTES } from "common/constants/routes";

import useBreakpoint from "antd/es/grid/hooks/useBreakpoint";

import { Button, Form, Tag } from "antd";

import AddUser from "./AddUser";
import EditUser from "./EditUser";
import { getUserDashboardSelection } from "common/utils/userDashboard";

import {
  successfulNotification,
  failedNotification,
} from "../../common/utils/notifications";
import { Helmet } from "react-helmet";
import { checkPermissions } from "../../common/utils/checkPermissions";
import PermissionWrapper from "shared/components/PermissionWrapper";
import { ExpandableTagList } from "shared/components/ExpandableTagList";
import "./manage-users.scss";
import CustomerApplicationsFilter from "filters/CustomerApplications.filter";
import LanguageCodeFilter from "filters/LanguageCode.filter";
import { fetchLanguageCodes } from "../../filters/LanguageCode.filter";
import {
  buildUserFormValues,
  buildUserPayloadFromForm,
  getDirectDashboardsToPersist,
  normalizeUserEditId as normalizeId,
} from "./userEditUtils";
import { saveUserEdits } from "./userEditService";

interface CustomerApplication {
  _id: string;
  name: string;
  active?: boolean;
  customerApplication?: string;
  [key: string]: any;
}

interface UserRow {
  _id?: string;
  id?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  targetLanguages?: string[];
  admin?: boolean;
  vendorManager?: boolean;
  isActive?: boolean;
  deleted?: boolean;
  customerApplications?: CustomerApplication[];
  roles?: any[];
  dashboards?: any[];
  userLevelPermissions?: any[];
  createdAt?: string;
  updatedAt?: string;
  lastLogin?: string;
  [key: string]: any;
}

interface Permission {
  id: string;
  key: string | number;
  customerApplication?: string;
  [key: string]: any;
}

interface Role {
  id?: string | number;
  _id?: string | number;
  name?: string;
  customerApplication?: string;
  dashboards?: (string | number)[];
  isActive?: boolean;
  description?: string;
  [key: string]: any;
}

interface Dashboard {
  id: string | number;
  _id?: string | number;
  key: string | number;
  name?: string;
  customerApplication?: string;
  [key: string]: any;
}

interface EditingState {
  editingRow: UserRow;
  userLevelPermissions: any[];
}

const renderBooleanIcon = (value: boolean | undefined) => {
  if (value) {
    return <CheckOutlined style={{ color: "var(--tertiary-color)" }} />;
  }
  return <CloseOutlined style={{ color: "var(--red)" }} />;
};


class ManageUsers extends TableAbstract<UserRow> {

  constructor(props: any) {
    super(props);

    const col = this.state as any;

    col.pinnedRight = ["edit"];
    col.pinnedColumns = [{ id: "edit", fixed: "right" }];

    const editCol = this.columns.find(c => c.id === "edit");

    if (editCol) {
      editCol.fixed = "right";
      editCol.width = editCol.width ?? 120;
    }
  }

  onFinishEditing = async (values: Record<string, any>) => {

    const { userFormData } = this.props as any;
    const { editing, editingForm, rolesList } = userFormData;

    if (!editing?.editingRow) return false;

    const userId = editing.editingRow._id ?? editing.editingRow.id;
    if (!userId) {
      failedNotification("Error updating user", "User id is missing");
      return false;
    }

    return saveUserEdits({
      umsClient: (this.userContext as any).umsClient,
      socket: (this.userContext as any)?.socket,
      userId: String(userId),
      values,
      rolesList: rolesList || [],
      originalUser: editing.editingRow,
      canManagePrivileges: !!(this.userContext as any).isAdmin,
      onSaved: () => {
        editingForm?.resetFields();
        this.setState(
          { ...(this.state as any), selectedRows: [], selectedRowKeys: [] } as any,
          () => this.reload()
        );
      },
    });
  };

  closeEditDrawer = () => {
    const { userFormData } = this.props as any;
    const { setVisibleEditDrawer, setEditing, editingForm } = userFormData;

    setVisibleEditDrawer(false);
    setEditing(null);
    editingForm?.resetFields?.();
  };

  getRowId = (row?: { _id?: any; id?: any }) => (row && (row._id || row.id)) || undefined;

  isActiveRow = (editing: any, record: any) => Boolean(editing && (this.getRowId(editing.editingRow) === this.getRowId(record)));

  openEditDrawer = async (record: any) => {
    const { userFormData } = this.props as any;
    const {
      rolesList,
      dashboardsList,
      permissionsList,
      editingForm,
      setEditing,
      setVisibleEditDrawer,
    } = userFormData;

    const userId = record?._id ?? record?.id;
    let row = { ...record };

    // fetch latest user payload before opening edit drawer
    if (userId) {
      try {
            const { data } = await (this.userContext as any).umsClient.get(
              `/ums-v1/users/${encodeURIComponent(String(userId))}`
            );
        row = { ...data };
      } catch (error) {
        console.error("Error refreshing user details before edit: ", error);
      }
    }

    row = {
      ...row,
      roles: (row.roles || [])
        .map((role: any) => {
          const id = normalizeId(role) ?? role;
          const matchedRole = rolesList.find(
            (r: any) => String(normalizeId(r) ?? r._id ?? r.id) === String(id)
          );

          if (matchedRole && typeof role === "object") {
            return {
              ...matchedRole,
              ...role,
              _id: normalizeId(role) ?? normalizeId(matchedRole),
            };
          }

          if (matchedRole) {
            return matchedRole;
          }

          if (typeof role === "object") {
            return role;
          }

          return null;
        })
        .filter(Boolean),
    };

    const { originalUserDashboardIds, dashboardIds } = getUserDashboardSelection({
      row,
      rolesList,
      dashboardsList,
    });

    const initialFormValues: any = buildUserFormValues({
      user: row,
      rolesList,
      permissionsList,
      dashboardIds,
    });

    if (dashboardIds.length > 0) {
      row.dashboards = dashboardIds;
    }

    row.__originalUserDashboards = originalUserDashboardIds;

    if (editingForm) {
      editingForm.resetFields();
      editingForm.setFieldsValue(initialFormValues);
    }

    setEditing({
      editingRow: row,
      userLevelPermissions: row.userLevelPermissions || [],
    });
    setVisibleEditDrawer(true);
  };

  canEditUsers = (): boolean => {
    const userPermissions = this.props.context?.userDetails?.permissions || [];
    return checkPermissions(userPermissions, [
      { customerApplication: "Mercury", permissionName: "Users", action: "edit" },
    ]);
  };

  defaultSortField = "firstName";

  FilterComponents = [
    { id: "firstName", name: "firstName", title: "First Name", component: TextFilter, active: true },
    { id: "lastName", name: "lastName", title: "Last Name", component: TextFilter, active: true },
    { id: "email", name: "email", title: "Email", component: TextFilter, active: true },
    { id: "targetLanguages", name: "targetLanguages", title: "Target Languages", component: LanguageCodeFilter, active: true },
    { id: "isActive", name: "isActive", title: "Active", component: BooleanFilter, active: true },
    {
      id: "customerApplications", name: "customerApplications", title: "Customer Application", component: (props: any) => (
        <CustomerApplicationsFilter {...props} data={(this.props as any)?.userFormData?.applicationsList} />
      ), active: true
    },
  ];

  columns = [
    {
      id: "firstName",
      title: "First Name",
      dataIndex: "firstName",
      sorter: true,
      width: 130,
    },
    {
      id: "lastName",
      title: "Last Name",
      dataIndex: "lastName",
      width: 130,
      sorter: true,
    },
    {
      id: "email",
      title: "Email",
      width: 160,
      dataIndex: "email",
      sorter: true,
      render: (email: string, record: UserRow) => {
        const userId = record._id ?? record.id;
        if (!userId) return email;

        return (
          <Link
            to={`${ROUTES.MANAGE_USERS}/${userId}`}
            state={{ lastLogin: record.lastLogin }}
          >
            {email}
          </Link>
        );
      },
    },
    {
      id: "targetLanguages",
      title: "Target Languages",
      dataIndex: "targetLanguages",
      width: 180,
      render: (languages: string[] = []) => <ExpandableTagList items={languages} limit={5} />,
    },

    {
      id: "admin",
      title: "Admin",
      dataIndex: "admin",
      width: 100,
      sorter: true,
      render: (value: boolean) => renderBooleanIcon(value),
    },
    {
      id: "vendorManager",
      title: "Vendor Manager",
      dataIndex: "vendorManager",
      sorter: true,
      width: 130,
      render: (value: boolean) => renderBooleanIcon(value),
    },
    {
      id: "isActive",
      title: "Active",
      dataIndex: "isActive",
      sorter: true,
      width: 80,
      render: (value: boolean) => renderBooleanIcon(value),
    },
    {
      id: "deleted",
      title: "Deleted",
      dataIndex: "deleted",
      sorter: true,
      width: 80,
      render: (value: boolean) => renderBooleanIcon(value),
    },
    {
      id: "customerApplications",
      title: "Customer Application",
      dataIndex: "customerApplications",
      width: 160,
      fixed: undefined,
      render: (apps: CustomerApplication[] = []) => {
        const displayNames = apps
          .map((ca: any) => {
            if (!ca) return null;
            if (typeof ca === "string" || typeof ca === "number") return String(ca);
            return ca.name || ca.title || ca.label || ca._id || ca.id || "";
          })
          .filter(Boolean);

        return displayNames.map((name: string, index: number) => (
          <Tag key={`${name}-${index}`}>{name}</Tag>
        ));
      },
    },
    {
      id: "createdAt",
      title: "Creation Timestamp",
      dataIndex: "createdAt",
      sorter: true,
      fixed: undefined,
      width: 160,
      render: (text: string) => <TimezoneConverter date={text} />,
    },
    {
      id: "updatedAt",
      title: "Last update date",
      dataIndex: "updatedAt",
      sorter: true,
      fixed: undefined,
      width: 150,
      render: (text: string) => <TimezoneConverter date={text} />,
    },
    {
      id: "lastLogin",
      title: "Last Login",
      dataIndex: "lastLogin",
      sorter: true,
      fixed: undefined,
      width: 150,
      render: (text: string) => <TimezoneConverter date={text} />,
    },
    {
      id: "edit",
      title: "Edit",
      dataIndex: "edit",
      sorter: false,
      fixed: "right",
      width: 80,
      render: (_text: string, record: any) => {

        const hasEditAccess = this.canEditUsers();

        return (
          <PermissionWrapper
            {...(hasEditAccess && { tooltip: "Allows editing of this user" })}
            hasPermissions={hasEditAccess}
          >
            <Button
              size="small"
              type="link"
              onClick={() => this.openEditDrawer(record)}
              className="enabled-edit-button"
              disabled={!hasEditAccess}
              style={{ marginRight: 0 }}
              icon={<EditOutlined />}
            >
              Edit
            </Button>
          </PermissionWrapper>
        );
      },
    },
  ];


  renderActions() {
    const { userFormData } = this.props as any;

    const {
      applicationsList,
      rolesList,
      permissionsList,
      dashboardsList,
      visibleNewUserDrawer,
      setVisibleNewUserDrawer,
      newUserForm,
      visibleEditDrawer,
      editing,
      editingForm,
      editFormRef,
      languageCodes,
    } = userFormData;

    const showNewUserDrawer = () => setVisibleNewUserDrawer(true);
    const closeNewUserDrawer = () => setVisibleNewUserDrawer(false);

    const onFinishNewUser = async (values: Record<string, any>) => {
      const payload = buildUserPayloadFromForm(values);
      payload.dashboards = getDirectDashboardsToPersist(
        values?.dashboardsValue || [],
        values?.rolesValue || [],
        rolesList || [],
        []
      );

      try {
        await (this.userContext as any).umsClient.post(
          "/ums-v1/users",
          payload
        );
        successfulNotification(
          "User created",
          "User has been created successfully."
        );
        newUserForm?.resetFields();
        closeNewUserDrawer();
        this.reload();
      } catch (error: any) {
        failedNotification(
          "Error creating user",
          error?.toString?.() || "Unknown error"
        );
      }
    };

    const userPermissions = this.userContext.userDetails?.permissions || [];

    const hasCreateAccess = checkPermissions(userPermissions, [
      {
        customerApplication: "Mercury",
        permissionName: "Users",
        action: "create",
      },
    ]);

    return (
      <>
        {/* Add User (uses legacy AddUser.js UI & logic) */}
        <PermissionWrapper
          hasPermissions={hasCreateAccess}
          children={
            <AddUser
              handlers={{
                showNewUserDrawer,
                closeNewUserDrawer,
                onFinishNewUser,
              }}
              values={{
                visibleNewUserDrawer,
                newUserForm,
                applicationsList,
                rolesList,
                permissionsList,
                dashboardsList,
                hasCreateAccess,
                languageCodes,
              }}
            />
          }
        />

        {editing && (
          <EditUser
            handlers={{
              closeEditDrawer: this.closeEditDrawer,
              onFinishEditing: this.onFinishEditing,
            }}
            values={{
              visibleEditDrawer,
              editingForm,
              applicationsList,
              rolesList,
              permissionsList,
              dashboardsList,
              editing,
              editFormRef,
              languageCodes,
              canManagePrivileges: !!(this.userContext as any).isAdmin,
            }}
          />
        )}
      </>
    );
  }

  rowClassName = (_record: UserRow): string => {
    return "row";
  };

  async fetch(): Promise<any> {
    const { sortOrder, sortField, currentPage, itemsPerPage } =
      this.state as any;
    const sortDirection = !sortOrder || sortOrder === "ascend" ? 1 : -1;
    const [{ data: userData }] = await Promise.all([
      (this.userContext as any).umsClient.post(`/ums-v1/users/search`, {
        filter: this.filtersToQuery(),
        pagination: {
          pageNumber: currentPage || 1,
          resultsPerPage: itemsPerPage,
        },
        sort: {
          [sortField ?? "firstName"]: sortDirection,
        }
      })
    ]);

    const result = {
      data: {
        documents: userData?.documents,
        meta: userData?.meta,
      },
    };

    return result;
  }

  pageTitle = "Manage Users";
  title = "Manage Users";
  pageClass = "userTable";
}


const ManageUserTable: FC = () => {
  const context = useMercuryContext();
  const navigate = useNavigate();
  const location = useLocation();
  const breaks = useBreakpoint();
  const userPermissions = context.userDetails?.permissions || [];
  const canReadRoles = checkPermissions(userPermissions, [
    {
      customerApplication: "Mercury",
      permissionName: "Manage Roles",
      action: "read",
    },
  ]);

  const [applicationsList, setApplicationsList] = useState<CustomerApplication[]>([]);
  const [dashboardsList, setDashboardsList] = useState<Dashboard[]>([]);
  const [rolesList, setRolesList] = useState<Role[]>([]);
  const [permissionsList, setPermissionsList] = useState<Permission[]>([]);
  const [languageCodes, setLanguageCodes] = useState<{ title: string; value: string }[]>([]);

  const [visibleNewUserDrawer, setVisibleNewUserDrawer] = useState(false);
  const [visibleEditDrawer, setVisibleEditDrawer] = useState(false);
  const [editing, setEditing] = useState<EditingState | null>(null);
  const [listsReady, setListsReady] = useState(false);

  const [newUserForm] = Form.useForm();
  const [editingForm] = Form.useForm();
  const editFormRef = useRef<any>(null);

  const openUserEditorFromRoute = useCallback(async (userId: string) => {
    if (!userId) return;

    try {

      const { data } = await context.umsClient.get(
        `/ums-v1/users/${encodeURIComponent(userId)}`
      );
      const roleObjects = (data.roles || [])
        .map((role: any) => {
          const id = normalizeId(role) ?? role;
          const matchedRole = rolesList.find(
            (r: any) => String(normalizeId(r) ?? r._id ?? r.id) === String(id)
          );

          if (matchedRole && typeof role === "object") {
            return {
              ...role,
              ...matchedRole,
              _id: normalizeId(role) ?? normalizeId(matchedRole),
            };
          }

          if (matchedRole) {
            return matchedRole;
          }

          if (typeof role === "object") {
            return role;
          }

          return null;
        })
        .filter(Boolean);

      const customerApplicationObjects = (data.customerApplications || []).map((ca: any) => {
        const id = typeof ca === "string" || typeof ca === "number" ? ca : (ca._id || ca.id);
        return (
          applicationsList.find((a: any) => a._id === id || a.id === id) ||
          { ...(typeof ca === "object" ? ca : {}), _id: id }
        );
      });

      const row = { ...data, roles: roleObjects, customerApplications: customerApplicationObjects };
      const { originalUserDashboardIds, dashboardIds } = getUserDashboardSelection({
        row,
        rolesList,
        dashboardsList,
      });

      const initialFormValues: any = buildUserFormValues({
        user: row,
        rolesList,
        permissionsList,
        dashboardIds,
      });

      if (dashboardIds.length > 0) {
        row.dashboards = dashboardIds;
      }

      row.__originalUserDashboards = originalUserDashboardIds;

      editingForm.resetFields();
      editingForm.setFieldsValue(initialFormValues);
      setEditing({ editingRow: row, userLevelPermissions: row.userLevelPermissions || [] });
      setVisibleEditDrawer(true);
      navigate(ROUTES.MANAGE_USERS, { replace: true, state: undefined });
    } catch (error) {
      console.error("Error opening user for edit from route state: ", error);
    }
  }, [context.umsClient, editingForm, navigate, permissionsList, rolesList, applicationsList]);

  const fetchPermissionsList = useCallback(async () => {
    try {
      const { data } = await context.umsClient.get(`ums-v1/permissions`);

      const dataWithKey: Permission[] = data.map((item: any) => ({
        ...item,
        key: item.id,
      }));

      setPermissionsList(dataWithKey);
    } catch (error) {
      console.error("Error fetching permissions list: ", error);
    }
  }, [context.umsClient]);

  const fetchRolesList = useCallback(async () => {
    if (!canReadRoles) {
      setRolesList([]);
      return;
    }

    try {
      const { data } = await context.umsClient.get(`/ums-v1/roles`);

      const normalized = data.map((role: any) => ({
        ...role,
        key: role.id || role._id,
        value: role.id || role._id,
        label: role.name,
      }));

      setRolesList(normalized);
    } catch (error) {
      console.error("Error fetching roles list: ", error);
    }
  }, [canReadRoles, context.umsClient]);


  const fetchDashboardsList = useCallback(async () => {
    try {
      const { data } = await context.umsClient.get(`ums-v1/dashboards`);

      const dataWithKey: Dashboard[] = data.map((item: any) => ({
        ...item,
        key: item.id,
      }));

      setDashboardsList(dataWithKey);
    } catch (error) {
      console.error("Error fetching dashboards list: ", error);
    }
  }, [context.umsClient]);

  const fetchApplicationsList = useCallback(async () => {
    try {
      const { data } = await context.coreClient.get(
        "core-v1/customerApplications/paginate?page=1&limit=500"
      );
      const docs: CustomerApplication[] = data?.[0]?.documents || [];
      setApplicationsList(docs);
    } catch (error) {
      console.error("Error fetching applications list: ", error);
    }
  }, [context.coreClient]);

  useEffect(() => {
    fetchLanguageCodes(context)
      .then((codes: { title: string; value: string }[]) => setLanguageCodes(codes))
      .catch((error: any) => console.error("Error fetching language codes: ", error));
  }, [context]);

  useEffect(() => {
    // Wait until roles/applications/dashboards lists are loaded so the edit
    // drawer has the data it needs to render selected values correctly.
    const requestedUserId = (location.state as any)?.openUserEditId;
    if (!requestedUserId || !listsReady) return;
    openUserEditorFromRoute(requestedUserId);
  }, [location.state, listsReady, openUserEditorFromRoute]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!permissionsList.length) {
          await fetchPermissionsList();
        }
        if (canReadRoles && !rolesList.length) {
          await fetchRolesList();
        }
        if (!dashboardsList.length) {
          await fetchDashboardsList();
        }
        if (!applicationsList.length) {
          await fetchApplicationsList();
        }
      } catch (error) {
        console.error("Error in ManageUserTable fetchData: ", error);
      } finally {
        // canReadRoles depends on context.userDetails, which is still loading on a fresh
        // navigation; wait for it to settle so roles aren't skipped as "not readable" too early.
        if (!(context as any).isAuthLoading) {
          setListsReady(true);
        }
      }
    };

    fetchData();
  }, [
    fetchPermissionsList,
    fetchRolesList,
    fetchDashboardsList,
    fetchApplicationsList,
    canReadRoles,
    (context as any).isAuthLoading,
    permissionsList.length,
    rolesList.length,
    dashboardsList.length,
    applicationsList.length,
  ]);

  // re-fetch role/dashboard metadata whenever user details refresh
  useEffect(() => {
    if (!(context as any)?.userDetails?.updated_at) {
      return;
    }

    void fetchRolesList();
    void fetchDashboardsList();
  }, [context?.userDetails?.updated_at, fetchRolesList, fetchDashboardsList]);

  // refresh cached roles/dashboards after a role change
  useEffect(() => {
    const handleRoleUpdated = () => {
      void fetchRolesList();
      void fetchDashboardsList();
    };

    window.addEventListener("mercury-role-updated", handleRoleUpdated);
    return () => {
      window.removeEventListener("mercury-role-updated", handleRoleUpdated);
    };
  }, [fetchRolesList, fetchDashboardsList]);

  const ManageUsersAny = ManageUsers as any;

  return (
    <div className="manage-page manage-users-page">
      <Helmet>
        <title>Manage Users - Mercury © RWS</title>
      </Helmet>
      <ManageUsersAny
        id="ManageUsers"
        noSelect={true}
        context={context}
        useLegacyEndpoints={false}
        breaks={breaks}
        hasSidebar={true}
        useEstimatedRowHeight
        userFormData={{
          applicationsList,
          dashboardsList,
          rolesList,
          permissionsList,
          visibleNewUserDrawer,
          setVisibleNewUserDrawer,
          visibleEditDrawer,
          setVisibleEditDrawer,
          editing,
          setEditing,
          newUserForm,
          editingForm,
          editFormRef,
          languageCodes,
        }}
      />
    </div>
  );
};

export default ManageUserTable;
